import { createHarvestLogApi } from "@common/api/harvestLog";
import type { HttpTransport } from "@common/api/transport";
import type { ID } from "@common/types/api";
import type { CropTimelinesResponse } from "@common/types/CropTimeline";
import type { GardenDetailed } from "@common/types/Garden";
import type { HourlyForecastResponse } from "@common/types/HourlyWeather";
import type { OnboardingStep } from "@common/types/OnboardingSteps";
import type { CalendarWeatherResponse } from "@common/types/Weather";

type UpdateGardenParams = {
  id: ID;
  lockTasks?: boolean;
  lockPerennialTasks?: boolean;
  title?: string;
  squareFootGardeningEnabled?: boolean;
};

export const createGardensApi = (http: HttpTransport) => ({
  ...createHarvestLogApi(http),

  destroyGarden: (id: ID) => http.del(`/api/gardens/${id}`, {}),

  fetchCropTimelines: ({
    endDate,
    gardenId,
    startDate,
    today
  }: {
    gardenId: number;
    startDate: string;
    endDate: string;
    today: string;
  }) =>
    http.get<CropTimelinesResponse>(`/api/gardens/${gardenId}/crop_timelines`, {
      queryParams: { endDate, startDate, today }
    }),

  fetchGardens: () => http.get<GardenDetailed[]>("/api/gardens"),

  fetchHourlyForecast: ({ gardenId }: { gardenId: number }) =>
    http.get<HourlyForecastResponse>(`/api/gardens/${gardenId}/hourly_weather`),

  fetchOnboardingSteps: ({ fresh, gardenId }: { gardenId: number; fresh?: boolean }) =>
    http.get<OnboardingStep[]>(`/api/gardens/${gardenId}/onboarding_steps`, {
      queryParams: fresh ? { fresh: true } : {}
    }),

  fetchWeatherDays: ({
    endDate,
    gardenId,
    startDate
  }: {
    gardenId: number;
    startDate: string;
    endDate: string;
  }) =>
    http.get<CalendarWeatherResponse>(`/api/gardens/${gardenId}/calendar_weather`, {
      queryParams: { endDate, startDate }
    }),

  switchGarden: (id: ID) => http.patch(`/api/gardens/${id}/switch`, {}),

  updateGarden: ({ id, ...params }: UpdateGardenParams) =>
    http.put<GardenDetailed>(`/api/gardens/${id}`, { garden: params })
});
